import { SingleValueCardColor, SingleValueCardType } from "constants/index";

export type ColumnPeriodT = {
  name: string;
  period: [string, string];
};

export interface PerformanceComparisonTableProps {
  dateRange: [string | null, string | null];
  onColumnPeriodsChange: (periods: ColumnPeriodT[]) => void;
}

export interface PerformanceColumnSelectorModalProps {
  open: boolean;
  onClose: () => void;
  columnPeriods: ColumnPeriodT[];
  onChange: (columns: ColumnPeriodT[]) => void;
  dateRange: [string | null, string | null];
}

export interface StatisticState {
  dateRange: [string | null, string | null];
}

export interface IncomeSummaryProps {
  columnPeriods: ColumnPeriodT[];
}

export interface OtherStatisticProps {
  dateRange: [string | null, string | null];
  columnPeriods: ColumnPeriodT[];
}

// Report related interfaces
export interface ReportValue {
  date: string;
  beginning_value: number;
  value_change: number;
  cashflows: number;
  dividends: number;
  closing_price: number;
  nav_price: number;
  unit_balance: number;
  factor: number;
  stock_factor: number;
  time_weighted_return: number;
  time_weighted_return_stock: number;
}

export interface IncomeSummaryItem {
  name: string;
  value: ReportValue;
}

export interface GraphData {
  date: string;
  stock_value: number;
  option_value: number;
}

export interface ReportResponse {
  status: string;
  data: {
    report_data: string;
    income_summary: IncomeSummaryItem[];
    graph_data: GraphData[];
  };
  message: string;
}

export interface ReportParams {
  account_ids: number[];
  strategy_ids: number[];
  periods: {
    name: string;
    period: [string, string];
  }[];
}

export interface NewReportRequest {
  orion_account_id: number;
  strategy_id: number;
  start_date: string;
  end_date: string;
  report_type: ReportType;
  exclude_transactions?: OrionTransaction["id"][];
}

export interface NewReportSingleInfoCards {
  card_1: number;
  card_2: number;
  card_3: number;
  card_4: number;
  card_5: number;
  card_6: number;
}

export interface NewReportResponse {
  status: string;
  data: {
    single_info_cards: NewReportSingleInfoCards;
    graph_or_table_data: ReportChartDataPoint[];
    excel_url?: string;
  };
  message: string;
}

/** PDF report request: matches backend PDFReportSerializer */
export type ReportType = "stock" | "strategy";

export interface PDFReportSettingsInfo {
  client_name: string;
  account: string;
  period: [string, string];
  symbol: string;
  strategy_id?: number | null;
  orion_account_id?: number | null;
}

export interface PDFReportTableColumnRow {
  id: number;
  Period: string;
  "Stock Price": number;
  "Option Income": number;
  "Dividend Income": number;
  "Total Income": number;
  Comment: string;
}

export interface ReportChartDataPoint {
  date: string;
  option_total: number;
  stock_total: number;
}

export type PDFReportValueType =
  | "number"
  | "currency"
  | "percentage"
  | "shares";

export interface PDFReportClientInfoItem {
  label: string;
  value: string | number;
  type: PDFReportValueType;
  description: string;
}

export interface PDFReportValueItem {
  label: string;
  value: number;
  type: PDFReportValueType;
}

export interface PDFReportAddictionalComponent {
  type: "bar" | "donut";
  title: string;
  limit: number;
  data: PDFReportValueItem[];
}

export interface PDFReportThemeInfoItem {
  label: string;
  description: string;
  values: PDFReportValueItem[];
  addictional_component?: PDFReportAddictionalComponent | null;
}

export interface PDFReportRequest {
  type: ReportType;
  settings_info?: PDFReportSettingsInfo;
  table_columns?: PDFReportTableColumnRow[];
  chart_data?: ReportChartDataPoint[];
  client_info: PDFReportClientInfoItem[];
  theme_info?: PDFReportThemeInfoItem[];
}

// ALERT DOMAIN TYPES AND HELPERS
export type AlertLeg = {
  action?: string;
  expiration_date?: string;
  strike_price?: number | string | null;
};

export type AlertPayload = {
  contract?: {
    legs?: AlertLeg[];
  };
  [key: string]: unknown;
};

export type AlertType = {
  id: number;
  type?: string | null;
  status?: string | null;
  seen?: boolean;
  time_sent?: string | null;
  order_id?: string | null;
  symbol?: string | null;
  contract_type?: "C" | "P" | string | null;
  net_avg_price?: number | string | null;
  auto_extension_percentage?: number | null;
  auto_partial_close_percentage?: number | null;
  legs?: AlertLeg[];
  payload?: AlertPayload | string | null;
};

export const asText = (value: unknown) =>
  value === null || value === undefined ? "—" : String(value);

export const formatMoney = (value: unknown, decimals = 4) => {
  if (value === null || value === undefined || value === "") return "—";
  const num = Number(value);
  if (Number.isNaN(num)) return String(value);
  return `$${num.toFixed(decimals)}`;
};

export const contractLabel = (contractType?: string | null) => {
  if (contractType === "C") return "Call";
  if (contractType === "P") return "Put";
  return asText(contractType ?? "");
};

export enum PerformanceReportTypeEnum {
  STOCK = "stock",
  STRATEGY = "strategy",
}

export enum AdjustmentTypeEnum {
  INCEPTION = "INCEPTION",
  BUY = "BUY",
  SELL = "SELL",
}

export interface AdjustmentRow {
  id: number;
  adjustment_date: string;
  adjustment_type: AdjustmentTypeEnum;
  adjustment_amount: number;
  unit_balance: number;
}

export interface AccountHistoryRecord {
  id: number;
  account: number;
  strategy: number;
  adjustment_date: string;
  adjustment_type: string;
  adjustment_amount: number;
  unit_balance_after_adjustment: number;
  created_on?: string;
  created_by?: number | null;
  updated_on?: string;
  updated_by?: number | null;
}

export interface AccountHistoryQueryParams {
  account_id: number;
  strategy_id: number;
  ordering?: string;
}

export interface CreateAccountHistoryPayload {
  account: number;
  strategy: number;
  adjustment_date: string;
  adjustment_type: string;
  adjustment_amount: number;
}

export interface SingleValueCardProps {
  label: string;
  value: number;
  description: string;
  type: SingleValueCardType;
  color: SingleValueCardColor;
  onValueChange: (value: number) => void;
}
export interface MultiValuesCardProps {
  title: string;
  values: ReportValueCardProps[];
  description: string;
  additionalComponents: React.ReactNode | null;
}

export interface ReportValueCardProps {
  label: string;
  value: number | string;
  type: SingleValueCardType;
  onValueChange: (value: number | string) => void;
  fullWidthRow?: boolean;
  color?: SingleValueCardColor;
}

export interface ReportPdfResponse {
  download_url?: string;
  [key: string]: string[] | unknown;
}

export interface OrionTransactionParams {
  start_date: string;
  end_date: string;
  strategy_id: number;
  orion_account_id: number;
  report_type: ReportType;
  transaction_type_ids?: number[];
  page?: number;
  page_size?: number;
  ordering?: string;
  search?: string;
}

export interface OrionTransaction {
  id: number;
  status: string;
  transaction_type_id: number;
  transaction_date: string;
  settlement_date: string;
  transaction_amount: number;
  units_amount: number;
  nav_price: number;
  ticker: string;
  transaction_type: string;
  unit_balance: number;
}

export enum OrionTransactionTypeIds {
  BUY_EXCHANGE = 10,
  SELL_EXCHANGE = 53,
}
